import React from 'react';
import styled from 'styled-components';
import { Icon, Button, Tooltip, Color, Tag } from '@bb/elements-web';

export interface SubscriptionCardProps {
  /** Whether there was an error fetching the Answering Service subscription data. */
  fetchDataError?: boolean;
  /** Whether the user is subscribed to this plan */
  isSubscribed: boolean;
  /** Whether there is an available upgrade to this plan that the user can pay for */
  isUpgradeAvailable: boolean;
  /** Whether the subscription has been suspended for overdue debt and access should not be given to it */
  isSuspended: boolean;
  /** Whether this user is allowed to see the button to upgrade the subscription themself. */
  allowedToSelfUpgrade: boolean;
  /** Whether the action button should be disabled */
  isButtonDisabled?: boolean;
  /** SVG component to display for the subscription/product */
  image: React.ReactNode;
  /** Text to display in the status pill (only shown when subscribed) */
  statusText: string;
  /** Text to display in the tooltip if a subscription is suspended */
  suspensionTooltipText: string;
  /** Text to display next to the status pill (only shown when subscribed) */
  managedByText: string;
  /** Upgrade prompt text (only shown when not subscribed, e.g. "Upgrade to Live Concierge") */
  upgradeText: string;
  /** Plan name/title */
  planName: string;
  /** Plan description */
  description: string;
  /** Callback when button is clicked */
  onButtonClick: () => void;
}

/** An exported type for all individual cards feeding into this SubscriptionCard to import */
export type SubscriptionCardConfigType = Pick<
  SubscriptionCardProps,
  'planName' | 'description' | 'statusText' | 'isUpgradeAvailable'
> &
  Partial<Pick<SubscriptionCardProps, 'isSuspended' | 'suspensionTooltipText'>>;

/**
 * Displays a subscription card with image, status/upgrade info, plan details, and action button.
 *
 * Two visual states:
 * - Subscribed: White background, status pill, outlined button
 * - Not subscribed: Light blue background, lock icon + upgrade text, solid blue button
 */
const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  fetchDataError,
  isSubscribed,
  isUpgradeAvailable,
  isSuspended = false,
  allowedToSelfUpgrade,
  isButtonDisabled = false,
  image,
  statusText,
  suspensionTooltipText = '',
  managedByText,
  upgradeText,
  planName,
  description,
  onButtonClick,
}) => {
  const isActionDisabled = fetchDataError || isButtonDisabled || isSuspended;

  return (
    <Container
      isSubscribed={isSubscribed}
      isUpgradeAvailable={isUpgradeAvailable}
    >
      <ImageContainer>{image}</ImageContainer>

      <ContentContainer>
        <TopRow>
          <StatusRow>
            {
              // If a suspended subscription, show suspended pill
              isSuspended ? (
                <>
                  <SuspendedPill color="danger" dataCy="suspended-pill">
                    Suspended
                  </SuspendedPill>
                  {managedByText && (
                    <ManagedByText>{managedByText}</ManagedByText>
                  )}
                </>
              ) : // If an active subscription, show the little pill and text for who pays
              isSubscribed ? (
                <>
                  <StatusPill>
                    {isUpgradeAvailable && allowedToSelfUpgrade && (
                      <StyledIcon icon="sparkle" color="white" />
                    )}
                    {statusText}
                  </StatusPill>
                  {managedByText && (
                    <ManagedByText>{managedByText}</ManagedByText>
                  )}
                </>
              ) : (
                // If not an active subscription, show the lock icon and let users know they can sign up
                <>
                  <StyledIcon icon="padlock" />
                  <UpgradeText>{upgradeText}</UpgradeText>
                </>
              )
            }
          </StatusRow>
          <Tooltip
            title={
              fetchDataError
                ? 'There was an error fetching the Answering Service subscription for your organization. Please contact your brokerage.'
                : suspensionTooltipText
            }
          >
            <span>
              {
                // Show the outlined "View Details" button (they're already subscribed to the highest available plan)
                isSubscribed && !isUpgradeAvailable ? (
                  <Button
                    minWidth="auto"
                    size="small"
                    type="secondary"
                    onClick={onButtonClick}
                    disabled={isActionDisabled}
                    dataCy="view-details-button"
                  >
                    View Details
                  </Button>
                ) : (
                  allowedToSelfUpgrade && (
                    // Show the filled-in "Learn More" button (there is a potential upgrade we can upsell them)
                    <SolidButton
                      minWidth="auto"
                      size="small"
                      onClick={onButtonClick}
                      disabled={isActionDisabled}
                      dataCy="learn-more-button"
                    >
                      Learn More
                    </SolidButton>
                  )
                )
              }
            </span>
          </Tooltip>
        </TopRow>

        <PlanName>{planName}</PlanName>
        <Description>{description}</Description>
      </ContentContainer>
    </Container>
  );
};

const Container = styled.div<{
  isSubscribed: boolean;
  isUpgradeAvailable: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px;
  border: 1px solid
    ${({ isSubscribed, isUpgradeAvailable }) =>
      isSubscribed && !isUpgradeAvailable ? Color.LIGHTER_GREY : 'transparent'};
  border-radius: 8px;
  background: ${({ isSubscribed, isUpgradeAvailable }) =>
    isSubscribed && !isUpgradeAvailable
      ? '#ffffff'
      : 'linear-gradient(271deg, #E2E4FF -135.74%, #FFF 67.3%, #D2E1FF 121.03%)'};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const Description = styled.p`
  font-size: 16px;
  color: #585e72;
  margin: 0;
  line-height: 1.4;
  white-space: pre-wrap;
`;

const ImageContainer = styled.div`
  flex-shrink: 0;
  width: 173px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    max-width: 100%;
    height: auto;
  }
`;

const ManagedByText = styled.span`
  font-size: 16px;
  color: #585e72;
`;

const PlanName = styled.h3`
  font-size: 24px;
  color: #2b2b2b;
  font-weight: 600;
  margin: 8px 0 4px 0;
`;

const StyledIcon = styled(Icon)`
  display: flex;
  align-items: center;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusPill = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(
    270deg,
    #77ffff -58.2%,
    #2c6ef2 62.35%,
    #274680 129.51%
  );
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  padding: 2px 12px;
  border-radius: 4px;
  white-space: nowrap;
`;

const SuspendedPill = styled(Tag)`
  margin-top: 5px;
`;

const SolidButton = styled(Button)`
  border: none;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const UpgradeText = styled.span`
  font-size: 16px;
  color: #585e72;
`;

export default SubscriptionCard;
